import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_BAR = { im_plan: '#22C55E', leicht_hinten: '#F59E0B', kritisch_hinten: '#EF4444', ueber_plan: '#338edc' };
const STATUS_LABEL = { im_plan: 'Im Plan', leicht_hinten: 'Hinter Plan', kritisch_hinten: 'Kritisch', ueber_plan: 'Voraus' };
const STATUS_ICON = { im_plan: '\u2705', leicht_hinten: '\u26A0\uFE0F', kritisch_hinten: '\u274C', ueber_plan: '\u2B06\uFE0F' };

function shortName(name) {
  if (!name) return '';
  const p = name.split(' ');
  return p.length < 2 ? name : `${p[0]} ${p[p.length - 1][0]}.`;
}

function fmtTime(d) {
  return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDate(d) {
  return d.toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function LagerTVPage() {
  const [data, setData] = useState(null);
  const [zeit, setZeit] = useState(new Date());
  const prevIst = useRef({});

  // URL params
  const params = new URLSearchParams(window.location.search);
  const maxTop = parseInt(params.get('top')) || 5;

  // Clock every second
  useEffect(() => {
    const t = setInterval(() => setZeit(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Data polling every 60s
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/echtzeit/tv`);
        if (res.ok) {
          const json = await res.json();
          // Track previous IST for animation
          const prev = {};
          if (data?.kunden) data.kunden.forEach(k => { prev[k.kunde_id] = k.ist_pakete; });
          prevIst.current = prev;
          setData(json);
        }
      } catch (e) { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  // Wake lock
  useEffect(() => {
    let wl;
    (async () => { try { wl = await navigator.wakeLock?.request('screen'); } catch (e) { /* */ } })();
    return () => { wl?.release?.(); };
  }, []);

  if (!data) {
    return (
      <div style={{ background: '#0F172A', color: '#F8FAFC', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #338edc', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const kunden = data.kunden || [];
  const topKunden = kunden.slice(0, maxTop);
  const restKunden = kunden.slice(maxTop);
  const { schicht, gesamt, team, ticker } = data;

  return (
    <div style={{
      background: '#0F172A', color: '#F8FAFC', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif",
      display: 'flex', flexDirection: 'column', padding: '24px 32px', boxSizing: 'border-box', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #338edc 0%, #6366F1 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 18,
          }}>W</div>
          <span style={{ fontSize: 28, fontWeight: 700 }}>Lager-Dashboard</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 48, fontWeight: 700, letterSpacing: -1, lineHeight: 1 }}>{fmtTime(zeit)}</div>
          <div style={{ fontSize: 16, color: '#94A3B8', marginTop: 4 }}>{fmtDate(zeit)}</div>
        </div>
      </div>

      {/* Shift bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 24, padding: '10px 20px',
        background: '#1E293B', borderRadius: 12, border: '1px solid #334155',
        marginBottom: 16, fontSize: 16, flexShrink: 0,
      }}>
        <span style={{ color: '#94A3B8' }}>Schicht:</span>
        <span style={{ fontWeight: 600 }}>{schicht.beginn} – {schicht.ende}</span>
        <span style={{ color: '#94A3B8' }}>|</span>
        <span><b>{schicht.vergangen_h}h</b> vergangen</span>
        <span style={{ color: '#94A3B8' }}>|</span>
        <span><b>{schicht.verbleibend_h}h</b> verbleibend</span>
        <div style={{ flex: 1 }} />
        {/* Gesamt progress */}
        <span style={{ fontWeight: 700, fontSize: 20 }}>
          GESAMT: {gesamt.ist} / {gesamt.soll}
        </span>
        <span style={{ fontSize: 18, fontWeight: 600, color: gesamt.soll > 0 && gesamt.ist / gesamt.soll >= 0.9 ? '#22C55E' : gesamt.ist / gesamt.soll >= 0.5 ? '#F59E0B' : '#EF4444' }}>
          ({gesamt.soll > 0 ? Math.round(gesamt.ist / gesamt.soll * 100) : 0}%)
        </span>
      </div>

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', gap: 16, minHeight: 0, overflow: 'hidden' }}>

        {/* Left: Kunden cards */}
        <div style={{ flex: 3, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
          {topKunden.map(k => (
            <KundeCard key={k.kunde_id} kunde={k} prevIst={prevIst.current[k.kunde_id]} schichtEnde={schicht.ende} />
          ))}

          {/* Rest (compact) */}
          {restKunden.length > 0 && (
            <div style={{
              background: '#1E293B', borderRadius: 12, border: '1px solid #334155', padding: '12px 20px',
            }}>
              <div style={{ fontSize: 14, color: '#94A3B8', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Weitere Kunden ({restKunden.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px' }}>
                {restKunden.map(k => {
                  const pct = k.soll_pakete > 0 ? Math.round(k.ist_pakete / k.soll_pakete * 100) : 0;
                  const sc = STATUS_BAR[k.prognose_status] || '#338edc';
                  return (
                    <div key={k.kunde_id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, minWidth: 100 }}>{k.kunde_name}</span>
                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#334155', minWidth: 60 }}>
                        <div style={{ height: '100%', borderRadius: 4, background: sc, width: `${Math.min(100, pct)}%`, transition: 'width 1s ease' }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: sc, minWidth: 50, textAlign: 'right' }}>{k.ist_pakete}/{k.soll_pakete}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right: Team + Ticker */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 280, overflow: 'hidden' }}>
          {/* Team */}
          <div style={{ background: '#1E293B', borderRadius: 12, border: '1px solid #334155', padding: '16px 20px' }}>
            <div style={{ fontSize: 14, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
              Team
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <TeamStat label="Anwesend" value={team.anwesend} color="#22C55E" />
              <TeamStat label="Pause" value={team.in_pause} color="#F59E0B" />
              <TeamStat label="Urlaub" value={team.urlaub} color="#338edc" />
              <TeamStat label="Krank" value={team.krank} color="#EF4444" />
            </div>
          </div>

          {/* Ticker */}
          <div style={{ background: '#1E293B', borderRadius: 12, border: '1px solid #334155', padding: '16px 20px', flex: 1, overflow: 'hidden' }}>
            <div style={{ fontSize: 14, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
              Live-Ticker
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(ticker || []).slice(0, 8).map((ev, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 14, alignItems: 'flex-start' }}>
                  <span style={{ color: '#64748B', fontSize: 13, minWidth: 42, flexShrink: 0 }}>
                    {ev.zeit ? new Date(ev.zeit).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                  <span style={{ color: ev.typ === 'pick' ? '#338edc' : '#22D3EE', fontSize: 12, fontWeight: 600, minWidth: 36 }}>
                    {ev.typ === 'pick' ? 'PICK' : 'PACK'}
                  </span>
                  <span style={{ color: '#CBD5E1' }}>
                    {shortName(ev.name)} {ev.kunde ? `\u2014 ${ev.kunde}` : ''} ({ev.menge})
                  </span>
                </div>
              ))}
              {(!ticker || ticker.length === 0) && (
                <div style={{ color: '#475569', fontSize: 14, fontStyle: 'italic' }}>Noch keine Events heute</div>
              )}
            </div>
          </div>

          {/* Prognose summary */}
          <div style={{ background: '#1E293B', borderRadius: 12, border: '1px solid #334155', padding: '16px 20px' }}>
            <div style={{ fontSize: 14, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
              Prognose {schicht.ende}
            </div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>~{gesamt.prognose}</div>
            <div style={{ fontSize: 14, color: '#94A3B8', marginTop: 4 }}>
              {gesamt.prognose >= gesamt.soll ? '\u2705 Tagesziel erreichbar' : '\u26A0\uFE0F Unter Tagesziel'}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
      `}</style>
    </div>
  );
}

// ─── Kunde Detail Card ───
function KundeCard({ kunde: k, prevIst, schichtEnde }) {
  const pct = k.soll_pakete > 0 ? Math.min(100, Math.round(k.ist_pakete / k.soll_pakete * 100)) : 0;
  const sc = STATUS_BAR[k.prognose_status] || '#338edc';
  const label = STATUS_LABEL[k.prognose_status] || '';
  const icon = STATUS_ICON[k.prognose_status] || '';

  return (
    <div style={{
      background: '#1E293B', borderRadius: 12, border: '1px solid #334155',
      padding: '16px 24px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 600 }}>{k.kunde_name}</span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 32, fontWeight: 700 }}>{k.ist_pakete} / {k.soll_pakete}</span>
          <span style={{ fontSize: 24, fontWeight: 700, color: sc }}>{pct}%</span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ width: '100%', height: 20, borderRadius: 10, background: '#334155', marginBottom: 8 }}>
        <div style={{
          height: '100%', borderRadius: 10, background: sc,
          width: `${pct}%`, transition: 'width 1s ease',
        }} />
      </div>

      {/* Info row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#94A3B8' }}>Prognose {schichtEnde}:</span>
          <span style={{ fontWeight: 600 }}>~{k.prognose_tagesende}</span>
          <span style={{ fontSize: 14, color: sc, fontWeight: 500 }}>{icon} {label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#94A3B8' }}>Tempo:</span>
          <span style={{ fontWeight: 600 }}>{k.tempo_pakete_pro_stunde} Pkt/h</span>
        </div>
      </div>

      {/* Team */}
      {(k.picker.length > 0 || k.packer.length > 0) && (
        <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 14 }}>
          {k.picker.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#338edc', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Pick:</span>
              <span style={{ color: '#CBD5E1' }}>{k.picker.map(p => shortName(p.name)).join(' \u00B7 ')}</span>
            </div>
          )}
          {k.packer.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#22D3EE', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Pack:</span>
              <span style={{ color: '#CBD5E1' }}>{k.packer.map(p => shortName(p.name)).join(' \u00B7 ')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Team Stat Box ───
function TeamStat({ label, value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 14, color: '#94A3B8' }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 700, marginLeft: 'auto' }}>{value}</span>
    </div>
  );
}
